import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { track } from "../analytics";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { store, type RootState } from "../redux/store";
import {
  addNotification,
  fetchNotifications,
  fetchUnreadCount,
} from "../redux/slices/NotificationsSlice";
import { getExportStatusApi } from "../utils/exportApiHandlers";
import type {
  ExportDataSource,
  ExportEntityType,
} from "../utils/exportApiHandlers";
import {
  EXPORT_FAILED_GRACE_MS,
  EXPORT_POLL_INTERVAL_MS,
  isExportPollTimedOut,
} from "../utils/pollExportStatus";
import { buildExportCompletionNotification } from "../utils/buildExportCompletionNotification";
import {
  exportNoticeRetryDelaysMs,
  findExportNotification,
  showLiveNotificationToast,
  toastNewNotifications,
} from "../utils/notificationLiveRefresh";
import {
  isInFlightExportStatus,
  jobsToPersist,
  loadExportProgress,
  saveExportProgress,
  type TrackedExportJob,
} from "../utils/exportProgressStorage";

const POLL_INTERVAL_MS = EXPORT_POLL_INTERVAL_MS;

export interface StartExportInput {
  exportId: string;
  entityType: TrackedExportJob["entityType"];
  source: TrackedExportJob["source"];
  entityLabel: string;
  columnCount?: number;
  recipientEmail?: string | null;
  deliveryMode?: TrackedExportJob["deliveryMode"];
  canDownloadInApp: boolean;
  analyticsEntity?: TrackedExportJob["analyticsEntity"];
}

export interface ExportHistoryViewer {
  entityType: ExportEntityType;
  source: ExportDataSource;
  entityLabel: string;
  focusExportId?: string;
}

export interface ExportProgressContextValue {
  isManaged: boolean;
  jobs: TrackedExportJob[];
  historyViewer: ExportHistoryViewer | null;
  startExport: (input: StartExportInput) => void;
  minimizeExport: (exportId: string) => void;
  closeExport: (exportId: string) => void;
  expandExport: (exportId: string) => void;
  dismissExport: (exportId: string) => void;
  openExportHistory: (input: ExportHistoryViewer) => void;
  closeExportHistory: () => void;
}

const ExportProgressContext = createContext<ExportProgressContextValue>({
  isManaged: false,
  jobs: [],
  historyViewer: null,
  startExport: () => undefined,
  minimizeExport: () => undefined,
  closeExport: () => undefined,
  expandExport: () => undefined,
  dismissExport: () => undefined,
  openExportHistory: () => undefined,
  closeExportHistory: () => undefined,
});

function patchJob(
  jobs: TrackedExportJob[],
  exportId: string,
  patch: Partial<TrackedExportJob>
): TrackedExportJob[] {
  return jobs.map((job) =>
    job.exportId === exportId ? { ...job, ...patch } : job
  );
}

async function unwrapDispatch(result: unknown): Promise<void> {
  if (
    result &&
    typeof result === "object" &&
    "unwrap" in result &&
    typeof (result as { unwrap: unknown }).unwrap === "function"
  ) {
    await (result as { unwrap: () => Promise<unknown> }).unwrap();
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export const ExportProgressProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const dispatch = useAppDispatch();
  const userId = useAppSelector((state) => state.userProfile?.user?.id) as
    string | undefined;
  const tenantId = useAppSelector(
    (state) => state.userProfile?.user?.tenantId
  ) as string | undefined;

  const [jobs, setJobs] = useState<TrackedExportJob[]>([]);
  const [historyViewer, setHistoryViewer] =
    useState<ExportHistoryViewer | null>(null);
  const [hydratedUserId, setHydratedUserId] = useState<string | null>(null);
  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;
  const completedTrackedRef = useRef<Set<string>>(new Set());
  const notifiedRef = useRef<Set<string>>(new Set());
  const inFlightPollsRef = useRef<Set<string>>(new Set());
  const terminalFetchRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!userId) {
      setJobs([]);
      setHydratedUserId(null);
      completedTrackedRef.current = new Set();
      notifiedRef.current = new Set();
      inFlightPollsRef.current = new Set();
      terminalFetchRef.current = new Set();
      return;
    }
    const restored = loadExportProgress(userId);
    setJobs(restored);
    setHydratedUserId(userId);
    terminalFetchRef.current = new Set();
    inFlightPollsRef.current = new Set();
    restored.forEach((job) => {
      if (job.notified) notifiedRef.current.add(job.exportId);
      if (job.status === "COMPLETED" || job.status === "FAILED") {
        completedTrackedRef.current.add(job.exportId);
      }
    });
  }, [userId]);

  useEffect(() => {
    if (!userId || hydratedUserId !== userId) return;
    saveExportProgress(userId, jobsToPersist(jobs));
  }, [jobs, userId, hydratedUserId]);

  const refreshAndToastNotifications = useCallback(async () => {
    const previousIds = new Set(
      ((store.getState() as RootState).notifications?.items ?? []).map(
        (item) => item._id
      )
    );
    try {
      await Promise.all([
        unwrapDispatch(dispatch(fetchNotifications({ reset: true }))),
        unwrapDispatch(dispatch(fetchUnreadCount())),
      ]);
    } catch {
      // fail-open: fallback toast still covers this session
    }
    const items = (store.getState() as RootState).notifications?.items ?? [];
    toastNewNotifications(items, previousIds);
    return items;
  }, [dispatch]);

  const announceExportOutcome = useCallback(
    async (job: TrackedExportJob, failed: boolean) => {
      if (notifiedRef.current.has(job.exportId)) return;
      notifiedRef.current.add(job.exportId);

      for (const delay of exportNoticeRetryDelaysMs()) {
        if (delay > 0) await sleep(delay);
        const items = await refreshAndToastNotifications();
        const serverNotice = findExportNotification(items, job.exportId);
        if (serverNotice) {
          showLiveNotificationToast(serverNotice);
          return;
        }
      }

      const fallback = buildExportCompletionNotification({
        exportId: job.exportId,
        entityType: job.entityType,
        entityLabel: job.entityLabel,
        source: job.source,
        failed,
        recipientUserId: userId,
        tenantId,
      });
      try {
        dispatch(addNotification(fallback));
      } catch {
        // drawer insert must never break export tracking
      }
      showLiveNotificationToast(fallback);
    },
    [dispatch, refreshAndToastNotifications, tenantId, userId]
  );

  const trackOutcome = useCallback(
    (job: TrackedExportJob, status: "COMPLETED" | "FAILED") => {
      if (!job.analyticsEntity) return;
      if (completedTrackedRef.current.has(job.exportId)) return;
      completedTrackedRef.current.add(job.exportId);
      try {
        track("export_completed", {
          entity: job.analyticsEntity,
          export_id: job.exportId,
          status,
        });
      } catch {
        // fail-open
      }
    },
    []
  );

  const applyTerminalStatus = useCallback(
    (
      exportId: string,
      status: "COMPLETED" | "FAILED",
      extras: Partial<TrackedExportJob>
    ) => {
      const current = jobsRef.current.find((job) => job.exportId === exportId);
      if (!current || !isInFlightExportStatus(current.status)) return;

      trackOutcome(current, status);
      void announceExportOutcome(current, status === "FAILED");

      setJobs((prev) => {
        if (current.presentation !== "expanded") {
          return prev.filter((job) => job.exportId !== exportId);
        }
        return patchJob(prev, exportId, {
          ...extras,
          status,
          notified: true,
          pendingFailedAt: undefined,
        });
      });
    },
    [announceExportOutcome, trackOutcome]
  );

  const refreshTerminalDownload = useCallback(async (job: TrackedExportJob) => {
    if (job.presentation !== "expanded") return;
    if (job.status !== "COMPLETED" && job.status !== "FAILED") return;
    if (job.completedRecord) return;
    if (terminalFetchRef.current.has(job.exportId)) return;
    terminalFetchRef.current.add(job.exportId);
    try {
      const getStatus = getExportStatusApi(job.entityType, job.source);
      const data = await getStatus(job.exportId);
      const record = data.export;
      if (!record) return;
      setJobs((prev) => {
        const current = prev.find((item) => item.exportId === job.exportId);
        if (!current || current.presentation !== "expanded") return prev;
        if (record.status === "COMPLETED") {
          return patchJob(prev, job.exportId, {
            status: "COMPLETED",
            processedCount: record.processedCount ?? current.processedCount,
            completedRecord: record,
            errorMessage: null,
          });
        }
        if (record.status === "FAILED") {
          return patchJob(prev, job.exportId, {
            status: "FAILED",
            errorMessage:
              record.error || current.errorMessage || "Export failed.",
            completedRecord: undefined,
          });
        }
        return prev;
      });
    } catch {
      terminalFetchRef.current.delete(job.exportId);
    }
  }, []);

  useEffect(() => {
    if (!userId || hydratedUserId !== userId) return;
    jobs.forEach((job) => {
      void refreshTerminalDownload(job);
    });
  }, [jobs, userId, hydratedUserId, refreshTerminalDownload]);

  const pollOne = useCallback(
    async (job: TrackedExportJob) => {
      if (!isInFlightExportStatus(job.status)) return;
      if (inFlightPollsRef.current.has(job.exportId)) return;
      inFlightPollsRef.current.add(job.exportId);
      try {
        const latest = jobsRef.current.find(
          (item) => item.exportId === job.exportId
        );
        if (!latest || !isInFlightExportStatus(latest.status)) return;

        const startedAt = latest.startedAt;
        if (startedAt == null) {
          setJobs((prev) =>
            patchJob(prev, latest.exportId, { startedAt: Date.now() })
          );
        } else if (isExportPollTimedOut(startedAt)) {
          applyTerminalStatus(latest.exportId, "FAILED", {
            errorMessage:
              "Export timed out. Please check export history or try again.",
          });
          return;
        }

        const getStatus = getExportStatusApi(latest.entityType, latest.source);
        const data = await getStatus(latest.exportId);
        const record = data.export;
        if (!record) return;

        if (record.status === "COMPLETED") {
          applyTerminalStatus(latest.exportId, "COMPLETED", {
            processedCount: record.processedCount,
            completedRecord: record,
            errorMessage: null,
          });
          return;
        }

        if (record.status === "FAILED") {
          const current = jobsRef.current.find(
            (item) => item.exportId === latest.exportId
          );
          const failedSince = current?.pendingFailedAt ?? Date.now();
          if (Date.now() - failedSince < EXPORT_FAILED_GRACE_MS) {
            setJobs((prev) => {
              const existing = prev.find(
                (item) => item.exportId === latest.exportId
              );
              if (existing?.pendingFailedAt) return prev;
              return patchJob(prev, latest.exportId, {
                pendingFailedAt: failedSince,
              });
            });
            return;
          }
          applyTerminalStatus(latest.exportId, "FAILED", {
            errorMessage: record.error || "Export failed.",
          });
          return;
        }

        setJobs((prev) =>
          patchJob(prev, latest.exportId, {
            status: record.status,
            processedCount: record.processedCount,
            pendingFailedAt: undefined,
            errorMessage: null,
            startedAt: startedAt ?? Date.now(),
          })
        );
      } catch {
        // Keep the job in-flight — a transient error or tab throttle must
        // not look like the export was cancelled.
      } finally {
        inFlightPollsRef.current.delete(job.exportId);
      }
    },
    [applyTerminalStatus]
  );

  const inFlightKey = jobs
    .filter((job) => isInFlightExportStatus(job.status))
    .map((job) => job.exportId)
    .sort()
    .join(",");

  useEffect(() => {
    if (!inFlightKey) return undefined;

    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      jobsRef.current
        .filter((job) => isInFlightExportStatus(job.status))
        .forEach((job) => {
          void pollOne(job);
        });
    };

    tick();
    const intervalId = window.setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      tick();
    }, POLL_INTERVAL_MS);

    const onVisible = () => {
      if (typeof document !== "undefined" && document.hidden) return;
      tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [inFlightKey, pollOne]);

  const startExport = useCallback((input: StartExportInput) => {
    const next: TrackedExportJob = {
      exportId: input.exportId,
      entityType: input.entityType,
      source: input.source,
      entityLabel: input.entityLabel,
      columnCount: input.columnCount,
      recipientEmail: input.recipientEmail ?? null,
      deliveryMode: input.deliveryMode ?? null,
      canDownloadInApp: input.canDownloadInApp,
      analyticsEntity: input.analyticsEntity,
      presentation: "expanded",
      status: "QUEUED",
      notified: false,
      startedAt: Date.now(),
    };
    setJobs((prev) => {
      const without = prev.filter((job) => job.exportId !== input.exportId);
      const collapsed = without.map((job) =>
        job.presentation === "expanded"
          ? { ...job, presentation: "minimized" as const }
          : job
      );
      return [...collapsed, next];
    });
  }, []);

  const minimizeExport = useCallback((exportId: string) => {
    setJobs((prev) => patchJob(prev, exportId, { presentation: "minimized" }));
  }, []);

  const closeExport = useCallback((exportId: string) => {
    setJobs((prev) => patchJob(prev, exportId, { presentation: "hidden" }));
  }, []);

  const expandExport = useCallback((exportId: string) => {
    setJobs((prev) =>
      prev.map((job) => {
        if (job.exportId === exportId) {
          return { ...job, presentation: "expanded" };
        }
        if (job.presentation === "expanded") {
          return { ...job, presentation: "minimized" };
        }
        return job;
      })
    );
  }, []);

  const dismissExport = useCallback((exportId: string) => {
    notifiedRef.current.add(exportId);
    setJobs((prev) => prev.filter((job) => job.exportId !== exportId));
  }, []);

  const openExportHistory = useCallback((input: ExportHistoryViewer) => {
    setHistoryViewer(input);
  }, []);

  const closeExportHistory = useCallback(() => {
    setHistoryViewer(null);
  }, []);

  const value = useMemo<ExportProgressContextValue>(
    () => ({
      isManaged: true,
      jobs,
      historyViewer,
      startExport,
      minimizeExport,
      closeExport,
      expandExport,
      dismissExport,
      openExportHistory,
      closeExportHistory,
    }),
    [
      jobs,
      historyViewer,
      startExport,
      minimizeExport,
      closeExport,
      expandExport,
      dismissExport,
      openExportHistory,
      closeExportHistory,
    ]
  );

  return (
    <ExportProgressContext.Provider value={value}>
      {children}
    </ExportProgressContext.Provider>
  );
};

export function useExportProgress(): ExportProgressContextValue {
  return useContext(ExportProgressContext);
}
